import { Settings } from 'lucide-react';
import Link from 'next/link';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import type { Me } from '../../../../lib/me';
import { Avatar, Button, ProgressBar } from '../../ui';
import { EarnCreditsOffer } from '../../earn-credits/EarnCreditsOffer';
import { useAccountSummary } from '../hooks/use-account-summary';
import { useCreditsSummary } from '../hooks/use-credits-summary';
import styles from './MobileSidebar.module.css';

// The drawer's floor (Figma 8265:84671): the credits card over the account row,
// on a fade of the drawer's darkest step so the list scrolls away underneath.
export function MobileFooter({ me }: { me: Me | null }) {
  return (
    <div className={styles.footer}>
      <GenericEventsRouteProvider value="credits">
        <CreditsCard />
      </GenericEventsRouteProvider>
      <GenericEventsRouteProvider value="account">
        <AccountRow me={me} />
      </GenericEventsRouteProvider>
    </div>
  );
}

// Plan name + Upgrade, then the usage line over its bar. Upgrade is the card's
// affordance (a real DS button), so the card itself isn't a link.
function CreditsCard() {
  const credits = useCreditsSummary();
  if (!credits) return null;
  return (
    <div className={styles.creditsCard}>
      <div className={styles.creditsHead}>
        <span className={styles.planLabel}>{credits.planLabel}</span>
        {/* Upgrade stays primary and keeps its place; the earn-credits offer (red band only, behind
            its own gate) is a secondary control beside it, opening the phone share screen.
            The button is offered on every plan: neither native card gates it on free/paid either, and
            both hide only the whole card (on `creditsCap <= 0`, which is our "no meter" case).
            `upgrade.label` is what says Upgrade vs Switch. Parity here is the GATE, not the
            destination — native picks between the Symphony picker and the Wixel page itself (behind a
            confirmation), where web always opens /packages and that page makes the same choice. */}
        <span className={styles.creditsActions}>
          <EarnCreditsOffer
            entry={{ kind: 'sidebar_credits', fraction: credits.fraction }}
            pageName="menu"
            variant="mobile"
            size="tiny"
          />
          <Button size="tiny" href={credits.upgrade.route} linkAs={credits.upgrade.path} onClick={credits.upgrade.onClick}>
            {credits.upgrade.label}
          </Button>
        </span>
      </div>
      <ProgressBar
        progress={credits.progress}
        value={credits.fraction * 100}
        label={credits.usageLabel}
        decorative
      />
    </div>
  );
}

// The whole row opens Settings (and carries the hidden brand-toggle long press),
// with the design's settings gear as the trailing affordance.
function AccountRow({ me }: { me: Me | null }) {
  const { initials, displayName, photoUrl, onPhotoError, settings } = useAccountSummary(me);
  // Reserve the row's height before `me` resolves so the footer doesn't jump.
  if (!initials) return <div style={{ minHeight: 36 }} />;

  return (
    <Link
      href={settings.route}
      as={settings.path}
      onClick={settings.onClick}
      aria-label={settings.label}
      title={displayName}
      className={styles.accountRow}
    >
      <span className={styles.accountButton}>
        <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={30} />
        <span className={styles.accountName}>{displayName}</span>
      </span>
      <span className={styles.accountGear} aria-hidden>
        <Settings size={16} />
      </span>
    </Link>
  );
}
